import { Navigate, useLocation } from "react-router-dom";
import LoadingScreen from "../components/loading-screen";
import { useAuthContext } from "./useAuthContext";
import { useAllowedRouteRoots } from "../hooks/useAllowedNavLinks";

type RoleGuardProps = {
  children: React.ReactNode;
};

/**
 * RoleGuard restricts access to dashboard module routes based on role.
 * It derives allowed route roots from the centralized hook and denies navigation
 * when the current path's first segment is not permitted.
 */
export default function RoleGuard({ children }: RoleGuardProps) {
  const { isAuthenticated, isInitialized, user } = useAuthContext();
  const location = useLocation();
  const allowedRouteRoots = useAllowedRouteRoots(user);

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // Always allow the dashboard home/root page
  const pathname = location.pathname || "/";
  const segments = pathname.split("/").filter(Boolean);
  const rootSegment = segments[0] || "";

  // If allowedRouteRoots is null, user can access everything (admin or unknown role)
  const isAllowed =
    rootSegment === "" ||
    allowedRouteRoots === null ||
    allowedRouteRoots.includes(rootSegment);

  if (!isAllowed) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}